import React, { useMemo } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { formatPrice, PLISE_MONTAJ_OPTIONS, DEFAULT_PLISE_MONTAJ_OPTION, createDefaultPliseItem, getPliseMontajExtraPrice } from '../../../../utils';
import { handleNumericKeyDown, customSelectStyles, pliseMontajSelectStyles } from '../OrderFormUtils';

const PliseForm = ({
  newOrder,
  onFieldChange,
  isLoading,
  productOptions,
  handlePliseProduct1Select,
  handlePliseProduct2Select,
  pliseMontajExtras = {}
}) => {
  const getMontajLabel = (option) => {
    const extra = getPliseMontajExtraPrice(pliseMontajExtras, option);
    if (option === DEFAULT_PLISE_MONTAJ_OPTION || extra <= 0) return option;
    return `${option} (+${formatPrice(extra)} ₺)`;
  };

  const addPliseRow = () => {
    const newList = [...(newOrder.pliseItems || []), createDefaultPliseItem(Date.now())];
    onFieldChange('pliseItems', newList);
  };

  const updatePliseRow = (idx, updates) => {
    const newList = (newOrder.pliseItems || []).map((row, rowIdx) =>
      rowIdx === idx ? { ...row, ...updates } : row
    );
    onFieldChange('pliseItems', newList);
  };

  const montajOptions = useMemo(
    () => PLISE_MONTAJ_OPTIONS.map((option) => ({
      value: option,
      label: getMontajLabel(option)
    })),
    [pliseMontajExtras]
  );

  return (
    <>
      <div className="form-group">
        <label>Oda Adı *</label>
        <input
          type="text"
          value={newOrder.roomName || ''}
          onChange={(e) => onFieldChange('roomName', e.target.value)}
          required
          disabled={isLoading}
          placeholder="Örn: Salon"
        />
      </div>

      <div className="form-group">
        <label>1. Ürün Adı *</label>
        <CreatableSelect
          options={productOptions}
          value={
            productOptions.find(o => o.label === newOrder.pliseProduct1Name) ||
            (newOrder.pliseProduct1Name ? { value: newOrder.pliseProduct1Name, label: newOrder.pliseProduct1Name } : null)
          }
          onChange={(option) => {
            if (!option) {
              onFieldChange('pliseProduct1Name', '');
              onFieldChange('pliseProduct1Price', '');
            } else if (option.__isNew__) {
              onFieldChange('pliseProduct1Name', option.value);
              onFieldChange('pliseProduct1Price', '');
            } else {
              handlePliseProduct1Select(option.value);
            }
          }}
          isDisabled={isLoading} isClearable isSearchable placeholder="Ürün seçin veya yazın..." noOptionsMessage={() => "Ürün bulunamadı"} formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`} styles={customSelectStyles}
        />
      </div>

      <div className="form-group">
        <label>1. Ürün Fiyat (₺) *</label>
        <input
          type="number"
          step="0.01"
          min="0.01"
          value={parseInt(newOrder.pliseProduct1Price) === 0 ? '' : newOrder.pliseProduct1Price || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('pliseProduct1Price', e.target.value)}
          disabled={isLoading}
          required
        />
      </div>

      <div className="form-group">
        <label>1. Ürün Kumaş Varyantı</label>
        <input
          type="text"
          value={newOrder.pliseProduct1Variation || ''}
          onChange={(e) => onFieldChange('pliseProduct1Variation', e.target.value)}
          disabled={isLoading}
          placeholder="Renk/Desen"
        />
      </div>

      <div className="form-group">
        <label>Kasa Varyantı</label>
        <input
          type="text"
          value={newOrder.pliseCaseVariation || ''}
          onChange={(e) => onFieldChange('pliseCaseVariation', e.target.value)}
          disabled={isLoading}
          placeholder="Örn: Antrasit"
        />
      </div>

      <div className="form-group">
        <label>2. Ürün Adı</label>
        <CreatableSelect
          options={productOptions}
          value={
            productOptions.find(o => o.label === newOrder.pliseProduct2Name) ||
            (newOrder.pliseProduct2Name ? { value: newOrder.pliseProduct2Name, label: newOrder.pliseProduct2Name } : null)
          }
          onChange={(option) => {
            if (!option) {
              onFieldChange('pliseProduct2Name', '');
              onFieldChange('pliseProduct2Price', '');
            } else if (option.__isNew__) {
              onFieldChange('pliseProduct2Name', option.value);
              onFieldChange('pliseProduct2Price', '');
            } else {
              handlePliseProduct2Select(option.value);
            }
          }}
          isDisabled={isLoading} isClearable isSearchable placeholder="Ürün seçin veya yazın..." noOptionsMessage={() => "Ürün bulunamadı"} formatCreateLabel={(inputValue) => `"${inputValue}" olarak ekle`} styles={customSelectStyles}
        />
      </div>

      <div className="form-group">
        <label>2. Ürün Fiyat (₺)</label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={parseInt(newOrder.pliseProduct2Price) === 0 ? '' : newOrder.pliseProduct2Price || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('pliseProduct2Price', e.target.value)}
          disabled={isLoading}
        />
      </div>

      <div className="form-group">
        <label>2. Ürün Kumaş Varyantı</label>
        <input
          type="text"
          value={newOrder.pliseProduct2Variation || ''}
          onChange={(e) => onFieldChange('pliseProduct2Variation', e.target.value)}
          disabled={isLoading}
          placeholder="Renk/Desen"
        />
      </div>

      <div className="form-group">
        <label>Montaj Toplam (₺)</label>
        <input
          type="number"
          step="0.01"
          value={newOrder.mounting || ''}
          onChange={(e) => onFieldChange('mounting', e.target.value)}
          placeholder="0.00"
          disabled={isLoading}
        />
      </div>

      <div style={{ gridColumn: '1 / -1', padding: '15px', backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', marginTop: '20px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h4 style={{ margin: 0, fontSize: '16px', color: '#0f172a', fontWeight: '700', display: 'flex', alignItems: 'center' }}>
            <i className="fas fa-layer-group" style={{ marginRight: '10px', color: '#b04309' }}></i>
            Plise Ölçü ve Adet Listesi
          </h4>
          <button
            type="button"
            onClick={addPliseRow}
            style={{ padding: '8px 16px', backgroundColor: '#0f172a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <i className="fas fa-plus"></i>
            Yeni Ölçü Ekle
          </button>
        </div>

        <div className="plise-batch-wrapper">
          <table className="plise-batch-table">
            <thead>
              <tr>
                <th>En (cm)</th>
                <th>Boy (cm)</th>
                <th>m²</th>
                <th>Adet</th>
                <th>Montaj Seçenekleri</th>
                <th style={{ textAlign: 'center' }}>Multisistem</th>
                <th style={{ textAlign: 'right' }}>Tutar (₺)</th>
                <th style={{ textAlign: 'center' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {Array.isArray(newOrder.pliseItems) && newOrder.pliseItems.length > 0 ? (
                newOrder.pliseItems.map((item, idx) => (
                  <tr key={item.id || idx} className="plise-row">
                    <td data-label="En (cm)">
                      <input
                        type="number"
                        value={item.width}
                        onChange={(e) => updatePliseRow(idx, { width: e.target.value })}
                        className="plise-input"
                        placeholder="En"
                      />
                    </td>
                    <td data-label="Boy (cm)">
                      <input
                        type="number"
                        value={item.height}
                        onChange={(e) => updatePliseRow(idx, { height: e.target.value })}
                        className="plise-input"
                        placeholder="Boy"
                      />
                    </td>
                    <td data-label="m²">
                      <div className="plise-m2-badge">{item.usedM2 || '1.00'}</div>
                    </td>
                    <td data-label="Adet">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updatePliseRow(idx, { quantity: e.target.value })}
                        className="plise-input qty-input"
                      />
                    </td>
                    <td data-label="Montaj Seçenekleri">
                      <div className="plise-montaj-select-wrap">
                        <Select
                          options={montajOptions}
                          value={montajOptions.find(
                            (o) => o.value === (item.montajOption || DEFAULT_PLISE_MONTAJ_OPTION)
                          )}
                          onChange={(option) => {
                            updatePliseRow(idx, {
                              montajOption: option?.value || DEFAULT_PLISE_MONTAJ_OPTION
                            });
                          }}
                          isDisabled={isLoading}
                          isSearchable={false}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          menuPosition="fixed"
                          styles={pliseMontajSelectStyles}
                          classNamePrefix="plise-montaj"
                        />
                      </div>
                    </td>
                    <td data-label="Multisistem" style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={item.multisistem}
                        onChange={(e) => updatePliseRow(idx, { multisistem: e.target.checked })}
                        className="plise-checkbox"
                      />
                    </td>
                    <td data-label="Tutar (₺)">
                      <div className="plise-row-total">{formatPrice(item.rowTotal || 0)} ₺</div>
                    </td>
                    <td data-label="İşlemler">
                      <div className="plise-actions">
                        <button
                          type="button"
                          className="plise-action-btn copy"
                          title="Satırı Kopyala"
                          onClick={() => {
                            const rowToCopy = {
                              ...newOrder.pliseItems[idx],
                              id: Date.now() + Math.random(),
                              montajOption: newOrder.pliseItems[idx].montajOption || DEFAULT_PLISE_MONTAJ_OPTION
                            };
                            const newList = [...newOrder.pliseItems];
                            newList.splice(idx + 1, 0, rowToCopy);
                            onFieldChange('pliseItems', newList);
                          }}
                        >
                          <i className="fas fa-copy"></i>
                        </button>
                        <button
                          type="button"
                          className="plise-action-btn delete"
                          title="Satırı Sil"
                          onClick={() => {
                            const newList = newOrder.pliseItems.filter((_, i) => i !== idx);
                            onFieldChange('pliseItems', newList);
                          }}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="empty-message">
                    Henüz bir ölçü eklenmedi. Hızlıca eklemek için "Yeni Ölçü Ekle" butonunu kullanın.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="form-group">
        <label>İskonto (%)</label>
        <input
          type="number"
          min="0"
          max="100"
          value={newOrder.discount || ''}
          onKeyDown={handleNumericKeyDown}
          onChange={(e) => onFieldChange('discount', e.target.value)}
          disabled={isLoading}
        />
      </div>

      {newOrder.pliseProduct1Name && (
        <div className="form-group">
          <label>Toplam Tutar (₺)</label>
          <input
            type="text"
            value={formatPrice(newOrder.totalPrice)}
            readOnly
            className="readonly-field total-price"
          />
        </div>
      )}

      {newOrder.discount > 0 && (
        <div className="form-group">
          <label>İndirimli Tutar (₺)</label>
          <input
            type="text"
            value={formatPrice(newOrder.discountedPrice)}
            readOnly
            className="readonly-field discounted-price"
          />
        </div>
      )}
    </>
  );
};

export default PliseForm;
